import React, { useState } from 'react';
import MobileModal from './MobileModal';
import { Button } from './ui';
import api from '../services/api';

export type TransactionType = 'Add' | 'Remove' | 'Adjust' | 'Sale' | 'Damage' | 'Transfer';

export interface InventoryItemForAdjustment {
  id: number;
  wineName?: string;
  wine_name?: string;
  quantity: number;
  bottleSize?: string;
  bottle_size?: string;
}

const TRANSACTION_TYPES: { value: TransactionType; label: string }[] = [
  { value: 'Add', label: 'Add (+)' },
  { value: 'Remove', label: 'Remove (-)' },
  { value: 'Adjust', label: 'Adjust (±)' },
  { value: 'Sale', label: 'Sale' },
  { value: 'Damage', label: 'Damage' },
  { value: 'Transfer', label: 'Transfer' }
];

const LARGE_CHANGE_THRESHOLD = 100;

export interface InventoryAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: InventoryItemForAdjustment | null;
  onSuccess?: () => void;
}

const InventoryAdjustmentModal: React.FC<InventoryAdjustmentModalProps> = ({
  isOpen,
  onClose,
  item,
  onSuccess
}) => {
  const [transactionType, setTransactionType] = useState<TransactionType>('Add');
  const [quantityChange, setQuantityChange] = useState<string>('');
  const [reason, setReason] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmLarge, setConfirmLarge] = useState(false);

  const currentQty = item?.quantity ?? 0;
  const changeNum = parseInt(quantityChange, 10);
  const isValidChange = !Number.isNaN(changeNum);
  const isAdjust = transactionType === 'Adjust';
  const isOutgoing = ['Remove', 'Sale', 'Damage', 'Transfer'].includes(transactionType);
  const newQuantity = isAdjust
    ? (isValidChange ? changeNum : currentQty)
    : isOutgoing
      ? currentQty - (isValidChange ? changeNum : 0)
      : currentQty + (isValidChange ? changeNum : 0);
  const effectiveChange = newQuantity - currentQty;
  const wouldGoNegative = newQuantity < 0;
  const isLargeChange = Math.abs(effectiveChange) >= LARGE_CHANGE_THRESHOLD;
  const needsConfirm = isLargeChange && !confirmLarge;

  const getReasonPlaceholder = (): string => {
    switch (transactionType) {
      case 'Add':
        return 'e.g., New production run';
      case 'Sale':
        return 'e.g., Customer order #12345';
      case 'Damage':
        return 'e.g., Bottle breakage during storage';
      case 'Transfer':
        return 'e.g., Transfer to warehouse B';
      default:
        return 'Reason for this adjustment';
    }
  };

  const resetForm = () => {
    setTransactionType('Add');
    setQuantityChange('');
    setReason('');
    setReferenceNumber('');
    setNotes('');
    setError(null);
    setConfirmLarge(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;
    setError(null);

    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    if (transactionType !== 'Adjust' && !isValidChange) {
      setError('Enter a valid quantity change.');
      return;
    }
    if (transactionType === 'Adjust') {
      if (!isValidChange || changeNum < 0) {
        setError('Enter a valid new quantity (0 or more).');
        return;
      }
    } else if (wouldGoNegative) {
      setError('Quantity cannot go below zero.');
      return;
    }
    if (needsConfirm) {
      setConfirmLarge(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        transactionType,
        reason: reason.trim(),
        referenceNumber: referenceNumber.trim() || undefined,
        notes: notes.trim() || undefined
      };
      if (transactionType === 'Adjust') {
        payload.quantity = newQuantity;
      } else {
        payload.quantity = effectiveChange;
      }
      await api.post(`/api/inventory/${item.id}/adjust`, payload);
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save adjustment.');
    } finally {
      setSubmitting(false);
    }
  };

  // Display label: lot number for bottling-sourced items, or fallback to item id
  const wineName = item?.wineName ?? item?.wine_name ?? `Item #${item?.id}`;

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Adjust: ${wineName}`}
      size="md"
      footer={
        <div className="flex gap-3 w-full justify-end">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || wouldGoNegative || !reason.trim()}
          >
            {submitting ? 'Saving…' : 'Apply Adjustment'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-error-50 text-error-700 text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-normal text-monday-text-muted mb-1">
            Transaction Type
          </label>
          <select
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value as TransactionType)}
            className="w-full rounded-lg border border-monday-border bg-white px-3 py-2 text-sm text-monday-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {TRANSACTION_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-normal text-monday-text-muted mb-1">
              Current Quantity
            </label>
            <p className="text-lg font-semibold text-monday-text">{currentQty}</p>
          </div>
          <div>
            <label className="block text-sm font-normal text-monday-text-muted mb-1">
              {transactionType === 'Adjust' ? 'New Quantity' : 'Quantity Change'}
            </label>
            <input
              type="number"
              value={quantityChange}
              onChange={(e) => setQuantityChange(e.target.value)}
              min={transactionType === 'Adjust' ? 0 : undefined}
              step={1}
              placeholder={transactionType === 'Adjust' ? 'New total' : '+/- bottles'}
              className="w-full rounded-lg border border-monday-border bg-white px-3 py-2 text-sm text-monday-text focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-normal text-monday-text-muted mb-1">
            Reason <span className="text-error-500">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={getReasonPlaceholder()}
            className="w-full rounded-lg border border-monday-border bg-white px-3 py-2 text-sm text-monday-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-normal text-monday-text-muted mb-1">
            Reference Number (optional)
          </label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="Invoice, order, or transfer ID"
            className="w-full rounded-lg border border-monday-border bg-white px-3 py-2 text-sm text-monday-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-normal text-monday-text-muted mb-1">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-monday-border bg-white px-3 py-2 text-sm text-monday-text focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div
          className={`p-4 rounded-lg border ${
            newQuantity < 0
              ? 'bg-error-50 border-error-200'
              : newQuantity <= 50
                ? 'bg-warning-50 border-warning-200'
                : 'bg-success-50 border-success-200'
          }`}
        >
          <p className="text-sm font-medium text-monday-text">
            Preview: {currentQty} → {Math.max(0, newQuantity)} bottles
            {!isAdjust && isValidChange && (
              <span className="ml-1 text-monday-text-muted">
                ({effectiveChange >= 0 ? '+' : ''}{effectiveChange})
              </span>
            )}
          </p>
          {newQuantity >= 0 && newQuantity <= 50 && (
            <p className="text-xs text-warning-700 mt-1">Low stock level</p>
          )}
        </div>

        {needsConfirm && (
          <div className="p-3 rounded-lg bg-warning-50 border border-warning-200 text-sm text-warning-800">
            This is a large change. Click Apply again to confirm.
          </div>
        )}
      </form>
    </MobileModal>
  );
};

export default InventoryAdjustmentModal;
